import { createTheme, darken } from '@mui/material/styles';

// Tema do konq-ui — mesmo visual do konq-backoffice (theme/index.ts e
// createBrandedTheme.ts de lá). Sem branding: identidade HZero (preto + verde-limão).
// Com branding do tenant: paleta do cliente, mantendo os overrides de layout.

const HZERO_BLACK = '#0A0A0A';
const HZERO_LIME = '#CCFF00';
const HZERO_LIME_HOVER = '#B8E600';

// Inter (licença OFL) servida pelo próprio app via @fontsource-variable/inter (import em index.jsx).
const FONT_FAMILY = '"Inter Variable", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const background = { default: '#f4f6f8', paper: '#ffffff' };
const text = { primary: '#212b36', secondary: '#637381' };
const divider = '#e0e0e0';

const typography = {
  fontFamily: FONT_FAMILY,
  h1: { fontWeight: 700, fontSize: '2.25rem' },
  h2: { fontWeight: 700, fontSize: '2rem' },
  h3: { fontWeight: 700, fontSize: '1.75rem' },
  h4: { fontWeight: 700, fontSize: '1.5rem' },
  h5: { fontWeight: 600, fontSize: '1.25rem' },
  h6: { fontWeight: 600, fontSize: '1rem' },
  subtitle1: { fontWeight: 600 },
  body2: { color: text.secondary },
};

const shape = { borderRadius: 8 };

// Overrides de layout comuns às duas variantes (sem cor de marca).
const layoutComponents = {
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: { root: { backgroundImage: 'none' } },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
        borderRadius: 12,
      },
    },
  },
  MuiCardHeader: {
    defaultProps: { titleTypographyProps: { variant: 'h6' } },
  },
  MuiAppBar: {
    styleOverrides: {
      root: { boxShadow: 'none', borderBottom: `1px solid ${divider}` },
    },
  },
  MuiDrawer: {
    styleOverrides: { paper: { borderRight: 'none' } },
  },
  MuiChip: {
    styleOverrides: { root: { fontWeight: 500 } },
  },
  MuiDialogTitle: {
    styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600 } },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: 12,
        [theme.breakpoints.down('sm')]: {
          margin: 8,
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100% - 16px)',
          maxHeight: 'calc(100% - 16px)',
        },
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: { padding: 16 },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '12px 24px 16px',
        [theme.breakpoints.down('sm')]: { padding: 12, flexWrap: 'wrap' },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          'padding': 16,
          '&:last-child': { paddingBottom: 16 },
        },
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: { fontWeight: 600 },
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: { padding: '8px 6px', fontSize: '0.8125rem' },
      }),
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
    },
  },
  MuiLink: {
    defaultProps: { underline: 'hover' },
  },
  MuiFormControlLabel: {
    styleOverrides: { root: { marginLeft: 0 } },
  },
};

// Tema base HZero — preto + verde, com inversão de cores no hover.
const baseTheme = createTheme({
  palette: {
    primary: { main: HZERO_BLACK, light: '#3A3A3A', dark: '#000000', contrastText: HZERO_LIME },
    secondary: { main: HZERO_LIME, light: '#E0FF66', dark: HZERO_LIME_HOVER, contrastText: HZERO_BLACK },
    background,
    text,
    divider,
  },
  typography,
  shape,
  components: {
    ...layoutComponents,
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
        containedPrimary: {
          'backgroundColor': HZERO_BLACK,
          'color': HZERO_LIME,
          '&:hover': { backgroundColor: HZERO_LIME, color: HZERO_BLACK },
        },
        outlinedPrimary: {
          'borderColor': HZERO_BLACK,
          'color': HZERO_BLACK,
          '&:hover': { backgroundColor: HZERO_BLACK, color: HZERO_LIME, borderColor: HZERO_BLACK },
        },
        textPrimary: {
          'color': HZERO_BLACK,
          '&:hover': { backgroundColor: 'rgba(204, 255, 0, 0.15)' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          'color': HZERO_BLACK,
          '&:hover': { backgroundColor: 'rgba(204, 255, 0, 0.15)' },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          'borderRadius': 8,
          'margin': '2px 8px',
          '&.Mui-selected': {
            'backgroundColor': 'rgba(204, 255, 0, 0.20)',
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: HZERO_BLACK },
            '&:hover': { backgroundColor: 'rgba(204, 255, 0, 0.30)' },
          },
          '&:hover': { backgroundColor: 'rgba(204, 255, 0, 0.12)' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: 'rgba(204, 255, 0, 0.15)' } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { 'textTransform': 'none', 'fontWeight': 600, '&.Mui-selected': { color: HZERO_BLACK } },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: HZERO_BLACK, height: 3 } },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            'color': HZERO_BLACK,
            '& + .MuiSwitch-track': { backgroundColor: HZERO_LIME, opacity: 1 },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { '&.Mui-checked': { color: HZERO_BLACK } } },
    },
    MuiRadio: {
      styleOverrides: { root: { '&.Mui-checked': { color: HZERO_BLACK } } },
    },
  },
});

/**
 * Tema MUI do app. Sem branding (ou sem cor primária) devolve o tema HZero.
 * Com branding, cria um tema com a paleta do tenant e os mesmos overrides de layout,
 * mas sem a inversão preto↔verde do HZero — o MUI gera os hovers a partir da paleta.
 */
export function createAppTheme(branding) {
  if (!branding?.corPrimaria) return baseTheme;

  const primary = branding.corPrimaria;
  const secondary = branding.corSecundaria || branding.corDestaque || '#81b496';

  return createTheme({
    palette: {
      primary: { main: primary },
      secondary: { main: secondary },
      background,
      text,
      divider,
    },
    typography,
    shape,
    components: {
      ...layoutComponents,
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
          containedPrimary: {
            'backgroundColor': primary,
            'color': '#ffffff',
            '&:hover': { backgroundColor: darken(primary, 0.4), color: '#ffffff' },
          },
          outlinedPrimary: {
            'borderColor': primary,
            'color': primary,
            '&:hover': { backgroundColor: primary, color: '#ffffff', borderColor: primary },
          },
          textPrimary: {
            'color': primary,
            '&:hover': { backgroundColor: `${primary}10` },
          },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiTabs: {
        styleOverrides: { indicator: { height: 3 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 8, margin: '2px 8px' } },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: `${primary}0D` },
            '&.Mui-selected': {
              'backgroundColor': `${primary}14`,
              '&:hover': { backgroundColor: `${primary}1F` },
            },
          },
        },
      },
    },
  });
}

export default baseTheme;
